import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authAPI } from "../services/api";
import { Eye, EyeOff, CheckCircle, ArrowRight, ArrowLeft, Zap, Shield, RefreshCw } from "lucide-react";
import { ProTrackMark, StackLogo } from "../components/ProTrackLogo";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const INDUSTRIES = [
  "Oil & Gas","Construction","Healthcare","Information Technology",
  "Agriculture","Energy","Security Services","Legal Services",
  "Telecommunications","Engineering","Logistics & Supply Chain",
  "Financial Services","Education","Manufacturing","Other",
];

const TRIAL_PERKS = [
  "Federal Government tender sources",
  "AI-powered tender matching",
  "Email deadline alerts",
  "Up to 50 tender alerts a month",
  "No credit card required",
];

const PLAN_DETAILS = {
  basic:        { name:"Basic",        price:"₦15,000/mo", color:"#4f8ef7",  perks:["Up to 50 tenders/month","Federal Government sources","Email deadline alerts","Basic search & filters","1 user account"] },
  professional: { name:"Professional", price:"₦35,000/mo", color:"#00d4a1",  perks:["Unlimited tenders","All 38 scraper sources","WhatsApp + Email alerts","AI tender matching","5 user accounts"] },
  enterprise:   { name:"Enterprise",   price:"₦75,000/mo", color:"#ffd166",  perks:["Everything in Professional","Compliance hub & HSE reports","Bid generator (AI)","Market intelligence analytics","Unlimited users"] },
};

function passwordScore(pw) {
  const checks = [pw.length >= 8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)];
  return { score: checks.filter(Boolean).length };
}

function isValidNGPhone(phone) {
  return /^(\+?234|0)\d{10}$/.test(phone.replace(/[\s\-()]/g, ""));
}

// ── Step 3: Email Verification ─────────────────────────
function VerifyEmail({ email, onSuccess }) {
  const [otp, setOtp]         = useState(["","","","","",""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { setSession } = useAuth();

  const handleChange = (i, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp]; next[i] = val; setOtp(next);
    if (val && i < 5) document.getElementById(`reg-otp-${i+1}`)?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) document.getElementById(`reg-otp-${i-1}`)?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) { toast.error("Enter all 6 digits"); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/verify-otp?email=${encodeURIComponent(email)}&otp=${code}`, { method:"POST", credentials:"include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Invalid code");
      // Store access_token only — refresh_token is set as httpOnly cookie by backend
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      if (data.user?.role) localStorage.setItem("user_role", data.user.role);
      // Sync AuthContext's own state too, not just localStorage — this is
      // what isAuthenticated (and therefore PrivateRoute, which /onboarding
      // is wrapped in) actually reads. Without this line the redirect below
      // bounced straight back to /login instead (found 2026-09-25).
      setSession(data.user);
      toast.success("Email verified! Setting up your account...");
      onSuccess();
    } catch (err) {
      toast.error(err.message || "Invalid code. Try again.");
      setOtp(["","","","","",""]); document.getElementById("reg-otp-0")?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res  = await fetch(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`, { method:"POST" });
      const data = await res.json();
      toast.success(data.message || "New code sent!"); setOtp(["","","","","",""]);
      document.getElementById("reg-otp-0")?.focus();
    } catch { toast.error("Failed to resend. Please try again."); }
    setResending(false);
  };

  return (
    <div style={{ textAlign:"center" }}>
      <div style={{ width:"64px", height:"64px", background:"rgba(0,212,161,0.08)", border:"1px solid rgba(0,212,161,0.2)", borderRadius:"16px", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:"20px" }}>
        <Shield size={28} color="#00d4a1" />
      </div>
      <h1 style={{ fontSize:"24px", fontWeight:"800", color:"#fff", marginBottom:"8px" }}>Verify your email</h1>
      <p style={{ fontSize:"14px", color:"rgba(232,234,240,0.45)", lineHeight:"1.6", marginBottom:"28px" }}>
        We sent a 6-digit code to<br/><strong style={{ color:"#00d4a1" }}>{email}</strong>
      </p>
      <div style={{ display:"flex", gap:"10px", justifyContent:"center", marginBottom:"24px" }}>
        {otp.map((d,i) => (
          <input key={i} id={`reg-otp-${i}`} value={d}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            maxLength={1} autoFocus={i === 0}
            style={{ width:"48px", height:"56px", textAlign:"center", fontSize:"22px", fontWeight:"800", fontFamily:"monospace", background:"rgba(232,234,240,0.04)", border: d ? "2px solid #00d4a1" : "1px solid rgba(232,234,240,0.1)", borderRadius:"10px", color:"#e8eaf0", outline:"none" }}
          />
        ))}
      </div>
      <button onClick={handleVerify} disabled={loading}
        style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#00d4a1,#00b888)", color:"#080d1a", border:"none", borderRadius:"10px", fontWeight:"700", fontSize:"14px", cursor: loading ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", marginBottom:"16px", fontFamily:"inherit", opacity: loading ? 0.7 : 1 }}>
        {loading ? <><RefreshCw size={15} style={{ animation:"spin 0.9s linear infinite" }}/> Verifying...</> : <><CheckCircle size={15}/> Verify Email</>}
      </button>
      <button onClick={handleResend} disabled={resending}
        style={{ background:"none", border:"none", color:"#00d4a1", cursor:"pointer", fontSize:"13px", fontWeight:"600", fontFamily:"inherit" }}>
        {resending ? "Sending..." : "Resend code"}
      </button>
      <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

export default function Register() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = new URLSearchParams(location.search);
  const intent    = params.get("intent");
  const planId    = params.get("plan") || "professional";
  const isSubscribeFlow = intent === "subscribe";
  const planInfo  = PLAN_DETAILS[planId] || PLAN_DETAILS.professional;

  const [step, setStep]                 = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [form, setForm] = useState({ full_name:"", email:"", phone:"", password:"", company_name:"", industry:"", rc_number:"", state:"" });

  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleRegister = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setLoading(true);
    try {
      await authAPI.register({ full_name:form.full_name, email:form.email, phone:form.phone, password:form.password, company_name:form.company_name });
      setRegisteredEmail(form.email);
      setStep(3);
    } catch (err) {
      const d = err.response?.data;
      toast.error(err.response?.status === 429 ? "Too many attempts. Please wait." : Array.isArray(d?.detail) ? d.detail[0]?.msg : d?.detail || "Registration failed.");
    } finally { setLoading(false); }
  };

  const goNext = () => {
    if (!form.full_name.trim())               { toast.error("Full name is required"); return; }
    if (!form.email.trim())                   { toast.error("Email is required"); return; }
    if (!form.phone.trim())                   { toast.error("Phone number is required"); return; }
    if (!isValidNGPhone(form.phone))          { toast.error("Enter a valid Nigerian phone number (e.g. 08012345678)"); return; }
    if (form.password.length < 8)             { toast.error("Password must be at least 8 characters"); return; }
    if (passwordScore(form.password).score < 2) { toast.error("Password too weak — add uppercase and numbers"); return; }
    setStep(2);
  };

  const { score: pwScore } = passwordScore(form.password);
  const pwColors = ["#ff4757","#ff8c42","#ffd166","#00d4a1"];
  const pwLabels = ["Too weak — add uppercase, numbers & symbols","Fair — add numbers or a special character","Good — add a special symbol for full strength","Strong password ✓"];

  return (
    <div style={{ minHeight:"100vh", background:"#080d1a", display:"flex", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box}
        .reg-input{width:100%;padding:11px 14px;background:rgba(232,234,240,0.04);border:1px solid rgba(232,234,240,0.1);border-radius:10px;color:#e8eaf0;font-size:14px;font-family:inherit;outline:none;transition:border-color 0.2s}
        .reg-input:focus{border-color:rgba(0,212,161,0.5);background:rgba(0,212,161,0.03)}
        .reg-input::placeholder{color:rgba(232,234,240,0.3)}
        .reg-select{width:100%;padding:11px 14px;background:rgba(232,234,240,0.04);border:1px solid rgba(232,234,240,0.1);border-radius:10px;color:#e8eaf0;font-size:14px;font-family:inherit;outline:none;appearance:none;cursor:pointer}
        .reg-select option{background:#141d2e}
        .reg-btn-primary{width:100%;padding:13px;border-radius:10px;border:none;background:linear-gradient(135deg,#00d4a1,#00b888);color:#080d1a;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity 0.2s;font-family:inherit}
        .reg-btn-primary:hover{opacity:0.88}
        .reg-btn-primary:disabled{opacity:0.5;cursor:not-allowed}
        .reg-btn-ghost{padding:13px 20px;border-radius:10px;border:1px solid rgba(232,234,240,0.12);background:transparent;color:rgba(232,234,240,0.6);font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-family:inherit}
        @media(max-width:768px){.reg-left-panel{display:none !important}.reg-right-panel{padding:32px 20px !important;width:100% !important}}
      `}</style>

      {/* Left panel */}
      <div className="reg-left-panel" style={{ width:"420px", flexShrink:0, background: isSubscribeFlow ? `linear-gradient(160deg,${planInfo.color}12 0%,transparent 60%)` : "linear-gradient(160deg,rgba(0,212,161,0.07) 0%,transparent 60%)", borderRight:"1px solid rgba(232,234,240,0.06)", padding:"48px 40px", display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"64px", cursor:"pointer" }} onClick={() => navigate("/")}>
            <ProTrackMark size={32}/>
            <div style={{ fontSize:"18px", fontWeight:"800", color:"#fff" }}>ProTrack<span style={{ color:"#00d4a1" }}>NG</span></div>
          </div>
          {isSubscribeFlow ? (
            <div>
              <div style={{ display:"inline-flex", alignItems:"center", gap:"7px", padding:"5px 14px", background:`${planInfo.color}18`, border:`1px solid ${planInfo.color}33`, borderRadius:"100px", fontSize:"12px", fontWeight:"700", color:planInfo.color, marginBottom:"20px" }}>
                <Zap size={11}/> {planInfo.name.toUpperCase()} PLAN
              </div>
              <h2 style={{ fontSize:"30px", fontWeight:"900", color:"#fff", lineHeight:"1.2", letterSpacing:"-1px", marginBottom:"8px" }}>Subscribe to {planInfo.name}</h2>
              <div style={{ fontSize:"26px", fontWeight:"900", color:planInfo.color, marginBottom:"16px" }}>{planInfo.price}</div>
              {planInfo.perks.map((p,i) => <div key={i} style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"10px" }}><CheckCircle size={13} color={planInfo.color}/><span style={{ fontSize:"13px", color:"rgba(232,234,240,0.7)" }}>{p}</span></div>)}
            </div>
          ) : (
            <div>
              <div style={{ display:"inline-flex", alignItems:"center", gap:"7px", padding:"5px 14px", background:"rgba(0,212,161,0.1)", border:"1px solid rgba(0,212,161,0.2)", borderRadius:"100px", fontSize:"12px", fontWeight:"700", color:"#00d4a1", marginBottom:"20px" }}>
                <span style={{ width:"6px", height:"6px", borderRadius:"50%", background:"#00d4a1", display:"inline-block" }}/> 14-DAY FREE TRIAL
              </div>
              <h2 style={{ fontSize:"30px", fontWeight:"900", color:"#fff", lineHeight:"1.2", letterSpacing:"-1px", marginBottom:"16px" }}>14 days to try it.<br/>No credit card.</h2>
              {TRIAL_PERKS.map((p,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"12px" }}>
                  <div style={{ width:"22px", height:"22px", borderRadius:"50%", background:"rgba(0,212,161,0.12)", border:"1px solid rgba(0,212,161,0.25)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <CheckCircle size={12} color="#00d4a1"/>
                  </div>
                  <span style={{ fontSize:"13px", color:"rgba(232,234,240,0.7)" }}>{p}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"8px", paddingTop:"32px", borderTop:"1px solid rgba(232,234,240,0.06)" }}>
          <span style={{ fontSize:"11px", color:"rgba(232,234,240,0.3)" }}>Powered by</span>
          <StackLogo size={16} variant="white"/>
          <span style={{ fontSize:"11px", color:"rgba(232,234,240,0.4)", fontWeight:"600" }}>ProStack NG Technologies</span>
        </div>
      </div>

      {/* Right panel */}
      <div className="reg-right-panel" style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"48px 40px" }}>
        <div style={{ width:"100%", maxWidth:"440px" }}>

          {/* Progress */}
          <div style={{ display:"flex", gap:"6px", marginBottom:"32px" }}>
            {[1,2,3].map(s => <div key={s} style={{ flex:1, height:"3px", borderRadius:"2px", background: s <= step ? "#00d4a1" : "rgba(232,234,240,0.1)", transition:"background 0.3s" }}/>)}
          </div>

          {/* Step 1 */}
          {step === 1 && (
            <>
              <div style={{ marginBottom:"28px" }}>
                <h1 style={{ fontSize:"26px", fontWeight:"800", color:"#fff", marginBottom:"6px" }}>Create your account</h1>
                <p style={{ fontSize:"14px", color:"rgba(232,234,240,0.45)" }}>Step 1 of 3 — Personal details</p>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
                <div>
                  <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>Full Name</label>
                  <input className="reg-input" value={form.full_name} onChange={e => update("full_name", e.target.value)} placeholder="Precious Amiekumo"/>
                </div>
                <div>
                  <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>Email Address</label>
                  <input className="reg-input" type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="your@company.com"/>
                </div>
                <div>
                  <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>Phone Number <span style={{ color:"#ff6b6b" }}>*</span></label>
                  <input className="reg-input" type="tel" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="08012345678 or +2348012345678"/>
                  <div style={{ fontSize:"11px", color:"rgba(232,234,240,0.3)", marginTop:"5px" }}>Each phone number can only be used for one account</div>
                </div>
                <div>
                  <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>Password</label>
                  <div style={{ position:"relative" }}>
                    <input className="reg-input" type={showPassword ? "text" : "password"} value={form.password} onChange={e => update("password", e.target.value)} placeholder="Min. 8 characters" style={{ paddingRight:"44px" }}/>
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"rgba(232,234,240,0.4)", cursor:"pointer", padding:"4px" }}>
                      {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                  {form.password ? (
                    <div style={{ marginTop:"8px" }}>
                      <div style={{ display:"flex", gap:"3px", marginBottom:"4px" }}>
                        {[0,1,2,3].map(i => <div key={i} style={{ flex:1, height:"3px", borderRadius:"2px", background: i < pwScore ? pwColors[pwScore-1] : "rgba(232,234,240,0.08)" }}/>)}
                      </div>
                      <div style={{ fontSize:"11px", color: pwScore > 0 ? pwColors[pwScore-1] : "rgba(232,234,240,0.3)" }}>{pwLabels[pwScore-1] || "Too short"}</div>
                    </div>
                  ) : <div style={{ fontSize:"11px", color:"rgba(232,234,240,0.3)", marginTop:"6px" }}>Use 8+ characters with uppercase, numbers & symbols</div>}
                </div>
                <button className="reg-btn-primary" style={{ marginTop:"8px" }} onClick={goNext}>Continue <ArrowRight size={16}/></button>
              </div>
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <>
              <div style={{ marginBottom:"28px" }}>
                <h1 style={{ fontSize:"26px", fontWeight:"800", color:"#fff", marginBottom:"6px" }}>Your company</h1>
                <p style={{ fontSize:"14px", color:"rgba(232,234,240,0.45)" }}>Step 2 of 3 — Business details</p>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
                <div>
                  <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>Company Name *</label>
                  <input className="reg-input" value={form.company_name} onChange={e => update("company_name", e.target.value)} placeholder="Your Company Ltd"/>
                </div>
                <div>
                  <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>Industry</label>
                  <select className="reg-select" value={form.industry} onChange={e => update("industry", e.target.value)}>
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
                  <div>
                    <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>RC Number</label>
                    <input className="reg-input" value={form.rc_number} onChange={e => update("rc_number", e.target.value)} placeholder="RC-123456"/>
                  </div>
                  <div>
                    <label style={{ fontSize:"12px", fontWeight:"600", color:"rgba(232,234,240,0.5)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"7px" }}>State</label>
                    <input className="reg-input" value={form.state} onChange={e => update("state", e.target.value)} placeholder="Rivers"/>
                  </div>
                </div>
                <div style={{ padding:"14px 16px", background: isSubscribeFlow ? `${planInfo.color}10` : "rgba(0,212,161,0.06)", border:`1px solid ${isSubscribeFlow ? planInfo.color+"25" : "rgba(0,212,161,0.15)"}`, borderRadius:"10px", display:"flex", gap:"10px", alignItems:"flex-start" }}>
                  <CheckCircle size={16} color={isSubscribeFlow ? planInfo.color : "#00d4a1"} style={{ flexShrink:0, marginTop:"1px" }}/>
                  <div>
                    <div style={{ fontSize:"13px", fontWeight:"700", color: isSubscribeFlow ? planInfo.color : "#00d4a1", marginBottom:"2px" }}>{isSubscribeFlow ? "Next: Verify email, then pay" : "14-day free trial starts now"}</div>
                    <div style={{ fontSize:"12px", color:"rgba(232,234,240,0.5)", lineHeight:"1.5" }}>{isSubscribeFlow ? "After verifying your email, complete payment to activate your plan." : "Your 14-day trial includes Basic plan features. Verify your email next."}</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:"10px", marginTop:"4px" }}>
                  <button className="reg-btn-ghost" onClick={() => setStep(1)}><ArrowLeft size={15}/> Back</button>
                  <button className="reg-btn-primary" onClick={handleRegister} disabled={loading} style={{ flex:1 }}>
                    {loading ? "Creating account..." : "Continue →"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Step 3 — Email verification */}
          {step === 3 && <VerifyEmail email={registeredEmail} onSuccess={() => navigate("/onboarding")}/>}

          {step < 3 && (
            <p style={{ textAlign:"center", marginTop:"24px", fontSize:"13px", color:"rgba(232,234,240,0.4)" }}>
              Already have an account?{" "}
              <span onClick={() => navigate("/login")} style={{ color:"#00d4a1", cursor:"pointer", fontWeight:"600" }}>Sign in</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
